import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, MapPin, Pencil, Trash2, Star, Building2, CheckCircle2, XCircle, Forklift, Key, Truck, Receipt, FileCheck, Loader2, User, Mail, Phone, Package, ClipboardList, CreditCard, AlertCircle, Clock, Home, MessageSquare, Wallet, ArrowRight, TrendingUp } from "lucide-react";
import { Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { SavedAddress, InsertSavedAddress } from "@shared/schema";
import { insertSavedAddressSchema, US_TIMEZONES } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatMoney } from "@shared/currency";
import { SalesTaxCertificateModal } from "@/components/sales-tax-certificate-modal";
import { CustomerClaimsSection } from "@/components/customer-claims";
import { CustomerOrdersSection } from "@/components/customer-orders";

const savedAddressFormSchema = insertSavedAddressSchema.omit({ 
  userId: true,
}).extend({
  phone: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const phoneRegex = /^[\d\s\-\(\)\.+]+$/;
    const digitsOnly = val.replace(/\D/g, '');
    return phoneRegex.test(val) && digitsOnly.length >= 10 && digitsOnly.length <= 15;
  }, { message: "Please enter a valid phone number (10-15 digits)" }),
});

type SavedAddressFormValues = z.infer<typeof savedAddressFormSchema>;

interface AccountAddress {
  id: string;
  accountId: string;
  isBilling: boolean;
  isShipping: boolean;
  locationName: string;
  line1: string;
  line2: string | null;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  phone: string | null;
  deliveryContactName: string | null;
  deliveryContactPhone: string | null;
  deliveryContactEmail: string | null;
  hasLoadingDock: boolean;
  hasForklift: boolean;
  isDefaultShipping: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface UserAccount {
  id: string;
  accountType: string;
  companyName: string;
  legalCompanyName: string | null;
  dbaName: string | null;
  ein: string | null;
  companyType: string | null;
  businessType: string | null;
  employeeCount: string | null;
  annualRevenue: string | null;
  hasLoadingDock: boolean;
  hasForklift: boolean;
  salesTaxExemptUrl: string | null;
  resaleCertificateState: string | null;
  resaleCertificateTaxId: string | null;
  resaleCertificateUrl: string | null;
  resaleCertificateAcknowledgedAt: string | null;
  notes: string | null;
  accountRepId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  isPrimaryContact: boolean;
  primaryContactName: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
}

const accountAddressFormSchema = z.object({
  isBilling: z.boolean().default(false),
  isShipping: z.boolean().default(true),
  locationName: z.string().min(1, "Location name is required"),
  line1: z.string().min(1, "Address line 1 is required"),
  line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  region: z.string().min(1, "State/Province is required"),
  postalCode: z.string().min(1, "Postal code is required"),
  country: z.string().default("US"),
  phone: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const phoneRegex = /^[\d\s\-\(\)\.+]+$/;
    const digitsOnly = val.replace(/\D/g, '');
    return phoneRegex.test(val) && digitsOnly.length >= 10 && digitsOnly.length <= 15;
  }, { message: "Please enter a valid phone number (10-15 digits)" }),
  deliveryContactName: z.string().optional(),
  deliveryContactPhone: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const phoneRegex = /^[\d\s\-\(\)\.+]+$/;
    const digitsOnly = val.replace(/\D/g, '');
    return phoneRegex.test(val) && digitsOnly.length >= 10 && digitsOnly.length <= 15;
  }, { message: "Please enter a valid delivery contact phone number (10-15 digits)" }),
  deliveryContactEmail: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(val);
  }, { message: "Please enter a valid email address" }),
  hasLoadingDock: z.boolean().default(false),
  hasForklift: z.boolean().default(false),
  isDefaultShipping: z.boolean().default(false),
}).refine((data) => data.isBilling || data.isShipping, {
  message: "Address must be either a billing or shipping address (or both)",
  path: ["isBilling"],
});

type AccountAddressFormValues = z.infer<typeof accountAddressFormSchema>;

// Format company type with proper casing (LLC all caps, others title case)
const formatCompanyType = (type: string): string => {
  if (!type) return type;
  const upperCaseTypes = ['LLC', 'LP', 'LLP', 'PC', 'PLLC'];
  const upper = type.toUpperCase();
  if (upperCaseTypes.includes(upper)) {
    return upper;
  }
  // Title case for others (e.g., "Corporation", "Sole Proprietorship")
  return type.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

// Format business type with proper casing (Retailer, Wholesaler, etc.)
const formatBusinessType = (type: string): string => {
  if (!type) return type;
  // Title case each word
  return type.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

// Format company name with proper casing: title case for words, uppercase for business suffixes
const formatCompanyName = (name: string): string => {
  if (!name) return name;
  
  // Business suffixes that should be uppercase
  const upperCaseSuffixes = ['LLC', 'LLP', 'LP', 'PC', 'PLLC', 'INC', 'LTD', 'CORP'];
  
  // First, title case all words
  const words = name.split(/\s+/).map(word => {
    // Check if this word (without trailing punctuation) is a business suffix
    const cleanWord = word.replace(/[.,]$/, '').toUpperCase();
    if (upperCaseSuffixes.includes(cleanWord)) {
      // Preserve any trailing punctuation
      const punctuation = word.match(/[.,]$/)?.[0] || '';
      return cleanWord + punctuation;
    }
    // Title case: first letter uppercase, rest lowercase
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
  
  return words.join(' ');
};

function PasswordChangeCard() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  
  const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d).{8,}$/;
  const PASSWORD_MESSAGE = "Password must be at least 8 characters with one uppercase letter and one number";
  
  const passwordChangeSchema = z.object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z.string().regex(PASSWORD_REGEX, PASSWORD_MESSAGE),
    confirmPassword: z.string().min(1, "Please confirm your new password"),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });
  
  type PasswordChangeFormValues = z.infer<typeof passwordChangeSchema>;
  
  const form = useForm<PasswordChangeFormValues>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });
  
  const passwordChangeMutation = useMutation({
    mutationFn: async (data: PasswordChangeFormValues) => {
      return await apiRequest('PATCH', '/api/users/me/password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
    },
    onSuccess: () => {
      toast({
        title: "Password changed",
        description: "Your password has been updated successfully",
      });
      setOpen(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Password change failed",
        description: error.message || "Failed to change password",
        variant: "destructive",
      });
    },
  });
  
  const onSubmit = (data: PasswordChangeFormValues) => {
    passwordChangeMutation.mutate(data);
  };
  
  return (
    <Card data-testid="card-password-change">
      <CardHeader className="p-3 sm:p-4 lg:p-5 pb-2">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Key className="h-4 w-4 sm:h-5 sm:w-5" />
          Password & Security
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">Manage your password and security settings</CardDescription>
      </CardHeader>
      <CardContent className="p-3 sm:p-4 lg:p-5 pt-0">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="button-change-password" className="h-9 sm:h-10 text-sm">
              Change Password
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="dialog-password-change">
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Current Password *</FormLabel>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="Enter your current password" 
                          {...field}
                          data-testid="input-current-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password *</FormLabel>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="Enter your new password (min 8 characters)" 
                          {...field}
                          data-testid="input-new-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm New Password *</FormLabel>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="Confirm your new password" 
                          {...field}
                          data-testid="input-confirm-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2 justify-end">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setOpen(false)}
                    data-testid="button-cancel-password"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    disabled={passwordChangeMutation.isPending}
                    data-testid="button-submit-password"
                  >
                    {passwordChangeMutation.isPending ? "Changing..." : "Change Password"}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function TimezoneSettingsCard({ user }: { user: any }) {
  const { toast } = useToast();
  const [selectedTimezone, setSelectedTimezone] = useState(user?.timezone || "America/Chicago");
  const [currentTime, setCurrentTime] = useState(new Date());
  
  useEffect(() => {
    if (user?.timezone) {
      setSelectedTimezone(user.timezone);
    }
  }, [user?.timezone]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  const timezoneMutation = useMutation({
    mutationFn: async (timezone: string) => {
      return await apiRequest('PATCH', '/api/users/me', { timezone });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast({
        title: "Timezone updated",
        description: "Your timezone preference has been saved",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error.message || "Failed to update timezone",
        variant: "destructive",
      });
    },
  });
  
  const handleTimezoneChange = (value: string) => {
    setSelectedTimezone(value);
    timezoneMutation.mutate(value);
  };
  
  const currentLabel = US_TIMEZONES.find(tz => tz.value === selectedTimezone)?.label || selectedTimezone;
  
  return (
    <Card data-testid="card-timezone-settings">
      <CardHeader className="p-3 sm:p-4 lg:p-5 pb-2">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Timezone Settings
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">Set your preferred timezone for displaying dates and times</CardDescription>
      </CardHeader>
      <CardContent className="p-3 sm:p-4 lg:p-5 pt-0">
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Select 
              value={selectedTimezone} 
              onValueChange={handleTimezoneChange}
              disabled={timezoneMutation.isPending}
            >
              <SelectTrigger className="w-full sm:w-[280px]" data-testid="select-timezone">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {US_TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value} data-testid={`option-timezone-${tz.value}`}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {timezoneMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            All dates and times in the application will be displayed in {currentLabel}.
          </p>
          <div className="mt-4 p-3 rounded-lg bg-muted/50 space-y-1">
            <p className="text-xs text-muted-foreground" data-testid="text-server-timestamp">
              <span className="font-medium">Server Timestamp:</span>{" "}
              {currentTime.toISOString()}
            </p>
            <p className="text-xs text-muted-foreground" data-testid="text-user-timestamp">
              <span className="font-medium">Your Timestamp:</span>{" "}
              {currentTime.toLocaleString('en-US', {
                timeZone: selectedTimezone,
                month: 'short',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
              })}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProfileEditCard({ user }: { user: any }) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);

  const profileFormSchema = z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z.string().optional().refine((val) => {
      if (!val || val === "") return true;
      const phoneRegex = /^[\d\s\-\(\)\.+]+$/;
      const digitsOnly = val.replace(/\D/g, '');
      return phoneRegex.test(val) && digitsOnly.length >= 10 && digitsOnly.length <= 15;
    }, { message: "Please enter a valid phone number (10-15 digits)" }),
    businessName: z.string().optional(),
  });

  type ProfileFormValues = z.infer<typeof profileFormSchema>;

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      phone: user?.phone || "",
      businessName: user?.businessName || "",
    },
  });

  const profileUpdateMutation = useMutation({
    mutationFn: async (data: ProfileFormValues) => {
      return await apiRequest('PATCH', '/api/users/me', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully",
      });
      setIsEditing(false);
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ProfileFormValues) => {
    profileUpdateMutation.mutate(data);
  };

  const handleCancel = () => {
    form.reset({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      phone: user?.phone || "",
      businessName: user?.businessName || "",
    });
    setIsEditing(false);
  };

  return (
    <Card className="mb-4 sm:mb-6 lg:mb-8" data-testid="card-account-info">
      <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-4 lg:p-6">
        <div>
          <CardTitle className="text-base sm:text-lg">Account Information</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Your profile details</CardDescription>
        </div>
        {!isEditing && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setIsEditing(true)}
            data-testid="button-edit-profile"
            className="h-8 sm:h-9 text-xs sm:text-sm"
          >
            <Pencil className="h-3 w-3 mr-1" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-3 sm:p-4 lg:p-6 pt-0">
        {isEditing ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter your first name" 
                          {...field}
                          data-testid="input-first-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter your last name" 
                          {...field}
                          data-testid="input-last-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter your phone number" 
                          {...field}
                          data-testid="input-phone"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="businessName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter your business name (optional)" 
                          {...field}
                          data-testid="input-business-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Email</p>
                <p className="text-base text-muted-foreground">{user?.email || "Not provided"}</p>
                <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCancel}
                  data-testid="button-cancel-profile"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={profileUpdateMutation.isPending}
                  data-testid="button-save-profile"
                >
                  {profileUpdateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </Form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Name</p>
              <p className="text-base" data-testid="text-user-name">
                {user?.firstName || user?.lastName 
                  ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
                  : "Not provided"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Email</p>
              <p className="text-base" data-testid="text-user-email">{user?.email || "Not provided"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Phone</p>
              <p className="text-base" data-testid="text-user-phone">{user?.phone || "Not provided"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Business</p>
              <p className="text-base" data-testid="text-user-business">{user?.businessName || "Not provided"}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface TaxExemptionStatus {
  accountId: string;
  companyName: string;
  isExempt: boolean;
  exemptionType: "w9" | "resale_certificate" | null;
  resaleCertificate: {
    state: string;
    taxId: string;
    url: string;
    acknowledgedAt: string;
  } | null;
  hasW9OnFile: boolean;
}

export default function UserAccount() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { toast } = useToast();
  const [taxCertificateModalOpen, setTaxCertificateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("account");

  useEffect(() => {
    document.title = `OpenBox by ShopJimmy`;
  }, []);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
    }
  }, [isAuthenticated, isLoading, toast]);

  const { data: userAccount, isLoading: accountLoading } = useQuery<UserAccount | null>({
    queryKey: ["/api/users/me/account"],
    enabled: isAuthenticated,
  });

  const { data: walletBalance } = useQuery<{ balance: string; expiringWithin30Days: string; nextExpiryDate: string | null }>({
    queryKey: ["/api/store-credit/balance"],
    enabled: isAuthenticated,
  });

  const { data: savedAddresses, isLoading: savedAddressesLoading, isError: savedAddressesError } = useQuery<SavedAddress[]>({
    queryKey: ["/api/users/addresses"],
    enabled: isAuthenticated,
  });

  const { data: accountAddresses, isLoading: accountAddressesLoading } = useQuery<AccountAddress[]>({
    queryKey: [`/api/accounts/${userAccount?.id}/addresses`],
    enabled: isAuthenticated && !!userAccount?.id,
  });

  const { data: taxExemptionStatus, isLoading: taxExemptionLoading } = useQuery<TaxExemptionStatus>({
    queryKey: ["/api/accounts", userAccount?.id, "tax-exemption"],
    enabled: isAuthenticated && !!userAccount?.id,
  });

  const isB2BUser = !!userAccount;

  if (isLoading || accountLoading) {
    return (
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="animate-pulse">
          <div className="h-6 sm:h-8 bg-gray-200 rounded w-1/4 mb-3 sm:mb-4"></div>
          <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 lg:py-6">
      <div className="mb-3 sm:mb-4 lg:mb-6">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-1">My Account</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">
          Welcome back, {user?.firstName && user?.lastName 
            ? `${user.firstName} ${user.lastName}` 
            : user?.email}
        </p>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="account" className="flex items-center gap-2" data-testid="tab-account">
            <User className="h-4 w-4 hidden sm:block" />
            Account
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2" data-testid="tab-orders">
            <Package className="h-4 w-4 hidden sm:block" />
            Orders
          </TabsTrigger>
          <TabsTrigger value="claims" className="flex items-center gap-2" data-testid="tab-claims">
            <ClipboardList className="h-4 w-4 hidden sm:block" />
            Claims
          </TabsTrigger>
          <TabsTrigger value="addresses" className="flex items-center gap-2" data-testid="tab-addresses">
            <MapPin className="h-4 w-4 hidden sm:block" />
            Addresses
          </TabsTrigger>
        </TabsList>

        {/* Account Tab Content */}
        <TabsContent value="account" className="space-y-4">

          {isB2BUser ? (
        <>
          {/* Account Status Banner + Wallet Card side by side */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 sm:gap-4 mb-3 sm:mb-4 lg:mb-6">
            <Card className="border-l-4 border-l-green-500" data-testid="card-account-status">
              <CardContent className="p-3 sm:p-4 lg:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-green-100 flex items-center justify-center">
                      <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold" data-testid="text-company-name">{userAccount.companyName}</h2>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Badge variant={userAccount.isActive ? "default" : "secondary"} className="text-xs">
                          {userAccount.isActive ? "Active Account" : "Inactive"}
                        </Badge>
                        <span>•</span>
                        <span>{userAccount.accountType === "BUSINESS" ? "Business Account" : "Consumer Account"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <p>Member since</p>
                    <p className="font-medium">{userAccount.createdAt ? new Date(userAccount.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Link href="/account/wallet">
              <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full" data-testid="card-wallet-summary">
                <CardContent className="p-3 sm:p-4 lg:p-5 h-full flex flex-col justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <Wallet className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <span className="font-medium text-sm">Store Credit</span>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${walletBalance && parseFloat(walletBalance.balance) > 0 ? "text-green-600 dark:text-green-400" : "text-foreground"}`} data-testid="text-wallet-balance-summary">
                      {walletBalance ? formatMoney(parseFloat(walletBalance.balance)) : "$0.00"}
                    </p>
                    {walletBalance && parseFloat(walletBalance.expiringWithin30Days) > 0 ? (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                        {formatMoney(parseFloat(walletBalance.expiringWithin30Days))} expiring soon
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-0.5">available balance</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span>View history</span>
                    <ArrowRight className="h-3 w-3" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Two-Column Layout for Company Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-3 sm:mb-4 lg:mb-6">
            {/* Company Details */}
            <Card data-testid="card-company-info">
              <CardHeader className="p-3 sm:p-4 lg:p-5 pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Building2 className="h-4 w-4 sm:h-5 sm:w-5" />
                  Company Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 lg:p-5 pt-0">
                <div className="space-y-3">
                  {userAccount.legalCompanyName && (
                    <div className="flex justify-between items-start border-b pb-2">
                      <span className="text-sm text-muted-foreground">Legal Name</span>
                      <span className="text-sm font-medium text-right" data-testid="text-legal-company-name">{formatCompanyName(userAccount.legalCompanyName)}</span>
                    </div>
                  )}
                  {userAccount.dbaName && (
                    <div className="flex justify-between items-start border-b pb-2">
                      <span className="text-sm text-muted-foreground">DBA</span>
                      <span className="text-sm font-medium text-right" data-testid="text-dba-name">{userAccount.dbaName}</span>
                    </div>
                  )}
                  {userAccount.ein && (
                    <div className="flex justify-between items-start border-b pb-2">
                      <span className="text-sm text-muted-foreground">EIN</span>
                      <span className="text-sm font-medium" data-testid="text-ein">{userAccount.ein}</span>
                    </div>
                  )}
                  {userAccount.companyType && (
                    <div className="flex justify-between items-start border-b pb-2">
                      <span className="text-sm text-muted-foreground">Company Type</span>
                      <span className="text-sm font-medium" data-testid="text-company-type">{formatCompanyType(userAccount.companyType)}</span>
                    </div>
                  )}
                  {userAccount.businessType && (
                    <div className="flex justify-between items-start border-b pb-2">
                      <span className="text-sm text-muted-foreground">Business Type</span>
                      <span className="text-sm font-medium" data-testid="text-business-type">{formatBusinessType(userAccount.businessType)}</span>
                    </div>
                  )}
                  {userAccount.employeeCount && (
                    <div className="flex justify-between items-start border-b pb-2">
                      <span className="text-sm text-muted-foreground">Employees</span>
                      <span className="text-sm font-medium" data-testid="text-employee-count">{userAccount.employeeCount}</span>
                    </div>
                  )}
                  {userAccount.annualRevenue && (
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-muted-foreground">Annual Revenue</span>
                      <span className="text-sm font-medium" data-testid="text-annual-revenue">{userAccount.annualRevenue}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Receiving Capabilities & Contact */}
            <div className="space-y-3 sm:space-y-4 lg:space-y-6">
              {/* Receiving Capabilities */}
              <Card data-testid="card-receiving-capabilities">
                <CardHeader className="p-3 sm:p-4 lg:p-5 pb-2">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Truck className="h-4 w-4 sm:h-5 sm:w-5" />
                    Receiving Capabilities
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 lg:p-5 pt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      {userAccount.hasLoadingDock ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      )}
                      <div>
                        <p className="text-sm font-medium">Loading Dock</p>
                        <p className="text-xs text-muted-foreground" data-testid="text-has-loading-dock">
                          {userAccount.hasLoadingDock ? "Available" : "Not available"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      {userAccount.hasForklift ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      )}
                      <div>
                        <p className="text-sm font-medium">Forklift</p>
                        <p className="text-xs text-muted-foreground" data-testid="text-has-forklift">
                          {userAccount.hasForklift ? "Available" : "Not available"}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Primary Contact */}
              <Card data-testid="card-primary-contact">
                <CardHeader className="p-3 sm:p-4 lg:p-5 pb-2">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <User className="h-4 w-4 sm:h-5 sm:w-5" />
                    Primary Contact
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 lg:p-5 pt-0">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm" data-testid="text-contact-name">{userAccount.primaryContactName || "Not provided"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm" data-testid="text-contact-email">{userAccount.primaryContactEmail || "Not provided"}</span>
                    </div>
                    {userAccount.primaryContactPhone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm" data-testid="text-contact-phone">{userAccount.primaryContactPhone}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-3 sm:mb-4 lg:mb-6">
            <Card data-testid="card-tax-exemption">
              <CardHeader className="p-3 sm:p-4 lg:p-5 pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Receipt className="h-4 w-4 sm:h-5 sm:w-5" />
                  Sales Tax Exemption
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Upload a resale certificate to remove sales tax from your purchases
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 lg:p-5 pt-0">
                {taxExemptionLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading tax exemption status...</span>
                  </div>
                ) : taxExemptionStatus?.isExempt ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-600">
                      <FileCheck className="h-5 w-5" />
                      <span className="font-medium">Your account is tax-exempt</span>
                    </div>
                    {taxExemptionStatus.exemptionType === "w9" && (
                      <p className="text-sm text-muted-foreground">
                        Tax exemption via W9 on file
                      </p>
                    )}
                    {taxExemptionStatus.exemptionType === "resale_certificate" && taxExemptionStatus.resaleCertificate && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-muted-foreground">State</p>
                            <p className="font-medium">{taxExemptionStatus.resaleCertificate.state}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Tax ID</p>
                            <p className="font-medium">{taxExemptionStatus.resaleCertificate.taxId}</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setTaxCertificateModalOpen(true)}
                          data-testid="button-manage-certificate"
                        >
                          Manage Certificate
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      No tax exemption on file. Upload your resale certificate to become tax-exempt.
                    </p>
                    <Button
                      variant="default"
                      onClick={() => setTaxCertificateModalOpen(true)}
                      data-testid="button-upload-certificate"
                    >
                      <Receipt className="h-4 w-4 mr-2" />
                      Upload Resale Certificate
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <PasswordChangeCard />
            <TimezoneSettingsCard user={user} />
          </div>

          <SalesTaxCertificateModal
            open={taxCertificateModalOpen}
            onOpenChange={setTaxCertificateModalOpen}
            accountId={userAccount.id}
          />

          <Separator className="my-4 sm:my-6 lg:my-8" />

          <div id="addresses" className="mb-4 sm:mb-6 lg:mb-8 scroll-mt-24">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">Addresses</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">Manage billing and shipping addresses</p>
              </div>
              <AccountAddressDialog mode="create" accountId={userAccount.id}>
                <Button data-testid="button-add-address" className="w-full sm:w-auto h-9 sm:h-10 text-sm">
                  <Plus className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Add Address
                </Button>
              </AccountAddressDialog>
            </div>

            {accountAddressesLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-40 sm:h-48 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : !accountAddresses || accountAddresses.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8 sm:py-12">
                  <MapPin className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">No addresses configured</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 text-center px-4">
                    Add billing and shipping addresses for your business
                  </p>
                  <AccountAddressDialog mode="create" accountId={userAccount.id}>
                    <Button data-testid="button-add-address" className="h-9 sm:h-10 text-sm">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Address
                    </Button>
                  </AccountAddressDialog>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {accountAddresses.map((address) => (
                  <Card key={address.id} className="relative" data-testid={`card-address-${address.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-5 w-5 text-muted-foreground" />
                          {address.isBilling && (
                            <Badge variant="secondary">Billing</Badge>
                          )}
                          {address.isShipping && (
                            <Badge variant="outline">Shipping</Badge>
                          )}
                          {address.isShipping && address.isDefaultShipping && (
                            <Badge variant="default">
                              <Star className="h-3 w-3 mr-1 fill-current" />
                              Default
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1 text-sm mb-4">
                        <p className="font-medium">{address.locationName}</p>
                        <p>{address.line1}</p>
                        {address.line2 && <p>{address.line2}</p>}
                        <p>
                          {address.city}, {address.region} {address.postalCode}
                        </p>
                        <p>{address.country}</p>
                        {address.phone && <p>Phone: {address.phone}</p>}
                      </div>

                      {/* Freight Capabilities */}
                      {(address.hasLoadingDock || address.hasForklift) && (
                        <div className="flex gap-3 mb-4 pb-4 border-b">
                          {address.hasLoadingDock && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Truck className="h-3 w-3" />
                              <span>Loading Dock</span>
                            </div>
                          )}
                          {address.hasForklift && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Forklift className="h-3 w-3" />
                              <span>Forklift</span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <AccountAddressDialog mode="edit" accountId={userAccount.id} address={address}>
                          <Button 
                            variant="outline" 
                            size="sm"
                            data-testid={`button-edit-address-${address.id}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </AccountAddressDialog>
                        <DeleteAccountAddressButton addressId={address.id} accountId={userAccount.id} />
                        {address.isShipping && !address.isDefaultShipping && (
                          <SetDefaultAccountAddressButton addressId={address.id} accountId={userAccount.id} />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <ProfileEditCard user={user} />

          {/* Store Credit Wallet Card for non-B2B users */}
          <Link href="/account/wallet">
            <Card className="hover:border-primary/50 transition-colors cursor-pointer" data-testid="card-wallet-summary">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <Wallet className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm sm:text-base">Store Credit Wallet</h3>
                      {walletBalance && parseFloat(walletBalance.expiringWithin30Days) > 0 ? (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          {formatMoney(parseFloat(walletBalance.expiringWithin30Days))} expiring soon
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">View your credit balance and history</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={`text-xl sm:text-2xl font-bold ${walletBalance && parseFloat(walletBalance.balance) > 0 ? "text-green-600 dark:text-green-400" : "text-foreground"}`} data-testid="text-wallet-balance-summary">
                        {walletBalance ? formatMoney(parseFloat(walletBalance.balance)) : "$0.00"}
                      </p>
                      <p className="text-xs text-muted-foreground">available</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 sm:mb-6 lg:mb-8">
            <PasswordChangeCard />
            <TimezoneSettingsCard user={user} />
          </div>

          <Separator className="my-4 sm:my-6 lg:my-8" />

          <div className="mb-4 sm:mb-6 lg:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">Saved Addresses</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">Manage your shipping addresses</p>
              </div>
              <SavedAddressDialog mode="create">
                <Button data-testid="button-add-address" className="w-full sm:w-auto h-9 sm:h-10 text-sm">
                  <Plus className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Add New Address
                </Button>
              </SavedAddressDialog>
            </div>

            {savedAddressesLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-40 sm:h-48 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : !savedAddresses || savedAddresses.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8 sm:py-12">
                  <MapPin className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">No saved addresses</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 text-center px-4">
                    Add a shipping address for faster checkout
                  </p>
                  <SavedAddressDialog mode="create">
                    <Button data-testid="button-add-address" className="h-9 sm:h-10 text-sm">
                      <Plus className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      Add New Address
                    </Button>
                  </SavedAddressDialog>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {savedAddresses.map((address) => (
                  <Card key={address.id} className="relative">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-5 w-5 text-muted-foreground" />
                          {address.label && (
                            <h3 className="font-semibold">{address.label}</h3>
                          )}
                          {address.isDefault && (
                            <Badge variant="secondary" className="ml-2">
                              <Star className="h-3 w-3 mr-1 fill-current" />
                              Default
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1 text-sm mb-4">
                        {address.companyName && (
                          <p className="font-medium">{address.companyName}</p>
                        )}
                        <p>{address.addressLine1}</p>
                        {address.addressLine2 && <p>{address.addressLine2}</p>}
                        <p>
                          {address.city}, {address.state} {address.zipCode}
                        </p>
                        {address.phone && <p>Phone: {address.phone}</p>}
                      </div>

                      <div className="flex gap-2">
                        <SavedAddressDialog mode="edit" address={address}>
                          <Button 
                            variant="outline" 
                            size="sm"
                            data-testid={`button-edit-address-${address.id}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </SavedAddressDialog>
                        <DeleteSavedAddressButton addressId={address.id} />
                        {!address.isDefault && (
                          <SetDefaultSavedAddressButton addressId={address.id} />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}
        </TabsContent>

        {/* Orders Tab Content */}
        <TabsContent value="orders">
          <CustomerOrdersSection />
        </TabsContent>

        {/* Claims Tab Content */}
        <TabsContent value="claims">
          <CustomerClaimsSection />
        </TabsContent>

        {/* Addresses Tab Content */}
        <TabsContent value="addresses">
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-foreground">Saved Addresses</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">Manage your shipping and billing addresses</p>
              </div>
              <SavedAddressDialog mode="create">
                <Button data-testid="button-add-address-tab" className="w-full sm:w-auto h-9 sm:h-10 text-sm">
                  <Plus className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Add New Address
                </Button>
              </SavedAddressDialog>
            </div>

            {savedAddressesLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-40 sm:h-48 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : savedAddressesError ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8 sm:py-12">
                  <AlertCircle className="h-10 w-10 sm:h-12 sm:w-12 text-destructive mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">Unable to load addresses</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 text-center px-4">
                    There was a problem loading your saved addresses. Please try again later.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => queryClient.invalidateQueries({ queryKey: ["/api/users/addresses"] })}
                    data-testid="button-retry-addresses"
                  >
                    Try Again
                  </Button>
                </CardContent>
              </Card>
            ) : !savedAddresses || savedAddresses.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8 sm:py-12">
                  <MapPin className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">No saved addresses</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 text-center px-4">
                    Add a shipping address for faster checkout
                  </p>
                  <SavedAddressDialog mode="create">
                    <Button data-testid="button-add-first-address" className="h-9 sm:h-10 text-sm">
                      <Plus className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      Add New Address
                    </Button>
                  </SavedAddressDialog>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {savedAddresses.map((address) => (
                  <Card key={address.id} className="relative" data-testid={`card-address-${address.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-5 w-5 text-muted-foreground" />
                          {address.label && (
                            <h3 className="font-semibold" data-testid={`text-address-label-${address.id}`}>{address.label}</h3>
                          )}
                          {address.isDefault && (
                            <Badge variant="secondary" className="ml-2" data-testid={`badge-default-${address.id}`}>
                              <Star className="h-3 w-3 mr-1 fill-current" />
                              Default
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1 text-sm mb-4">
                        {address.companyName && (
                          <p className="font-medium">{address.companyName}</p>
                        )}
                        <p data-testid={`text-address-line1-${address.id}`}>{address.addressLine1}</p>
                        {address.addressLine2 && <p>{address.addressLine2}</p>}
                        <p data-testid={`text-address-city-${address.id}`}>
                          {address.city}, {address.state} {address.zipCode}
                        </p>
                        {address.phone && <p>Phone: {address.phone}</p>}
                        {address.hasLoadingDock && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                            <Truck className="h-3 w-3" />
                            Loading Dock Available
                          </div>
                        )}
                        {address.hasForklift && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Forklift className="h-3 w-3" />
                            Forklift Available
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <SavedAddressDialog mode="edit" address={address}>
                          <Button 
                            variant="outline" 
                            size="sm"
                            data-testid={`button-edit-address-${address.id}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </SavedAddressDialog>
                        <DeleteSavedAddressButton addressId={address.id} />
                        {!address.isDefault && (
                          <SetDefaultSavedAddressButton addressId={address.id} />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SavedAddressDialog({ 
  children, 
  mode, 
  address 
}: { 
  children: React.ReactNode; 
  mode: "create" | "edit"; 
  address?: SavedAddress;
}) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const form = useForm<SavedAddressFormValues>({
    resolver: zodResolver(savedAddressFormSchema),
    defaultValues: address ? {
      label: address.label || "",
      companyName: address.companyName || "",
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || "",
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      phone: address.phone || "",
      isDefault: address.isDefault || false,
    } : {
      label: "",
      companyName: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      phone: "",
      isDefault: false,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: SavedAddressFormValues) => {
      return await apiRequest("POST", "/api/users/addresses", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users/addresses"] });
      toast({
        title: "Success",
        description: "Address added successfully",
      });
      setOpen(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add address",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: SavedAddressFormValues) => {
      return await apiRequest("PUT", `/api/users/addresses/${address?.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users/addresses"] });
      toast({
        title: "Success",
        description: "Address updated successfully",
      });
      setOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update address",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: SavedAddressFormValues) => {
    if (mode === "create") {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  useEffect(() => {
    if (open && address && mode === "edit") {
      form.reset({
        label: address.label || "",
        companyName: address.companyName || "",
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2 || "",
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
        phone: address.phone || "",
        isDefault: address.isDefault || false,
      });
    } else if (open && mode === "create") {
      form.reset({
        label: "",
        companyName: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
        phone: "",
        isDefault: false,
      });
    }
  }, [open, address, mode, form]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-address-form">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Address" : "Edit Address"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Label (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="e.g., Home, Office" 
                        {...field}
                        value={field.value || ""}
                        data-testid="input-label"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Company name" 
                        {...field}
                        value={field.value || ""}
                        data-testid="input-company"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="addressLine1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 1 *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Street address" 
                      {...field} 
                      data-testid="input-address1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="addressLine2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 2 (Optional)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Apartment, suite, etc." 
                      {...field}
                      value={field.value || ""}
                      data-testid="input-address2"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="City" 
                        {...field} 
                        data-testid="input-city"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="State" 
                        {...field} 
                        data-testid="input-state"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ZIP Code *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="ZIP code" 
                        {...field} 
                        data-testid="input-zip"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone (Optional)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Phone number" 
                      {...field}
                      value={field.value || ""}
                      data-testid="input-phone"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value || false}
                      onCheckedChange={field.onChange}
                      data-testid="checkbox-default"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Set as default address</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {(createMutation.isPending || updateMutation.isPending) 
                  ? "Saving..." 
                  : mode === "create" 
                    ? "Add Address" 
                    : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function AccountAddressDialog({ 
  children, 
  mode, 
  accountId,
  address 
}: { 
  children: React.ReactNode; 
  mode: "create" | "edit"; 
  accountId: string;
  address?: AccountAddress;
}) {
  const [open, setOpen] = useState(false);
  const [useMyContactInfo, setUseMyContactInfo] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const form = useForm<AccountAddressFormValues>({
    resolver: zodResolver(accountAddressFormSchema),
    defaultValues: address ? {
      isBilling: address.isBilling,
      isShipping: address.isShipping,
      locationName: address.locationName,
      deliveryContactName: address.deliveryContactName || "",
      deliveryContactPhone: address.deliveryContactPhone || "",
      deliveryContactEmail: address.deliveryContactEmail || "",
      line1: address.line1,
      line2: address.line2 || "",
      city: address.city,
      region: address.region,
      postalCode: address.postalCode,
      country: address.country,
      phone: address.phone || "",
      hasLoadingDock: address.hasLoadingDock || false,
      hasForklift: address.hasForklift || false,
      isDefaultShipping: address.isDefaultShipping || false,
    } : {
      isBilling: false,
      isShipping: true,
      locationName: "",
      deliveryContactName: "",
      deliveryContactPhone: "",
      deliveryContactEmail: "",
      line1: "",
      line2: "",
      city: "",
      region: "",
      postalCode: "",
      country: "US",
      phone: "",
      hasLoadingDock: false,
      hasForklift: false,
      isDefaultShipping: false,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: AccountAddressFormValues) => {
      return await apiRequest("POST", `/api/accounts/${accountId}/addresses`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/accounts/${accountId}/addresses`] });
      toast({
        title: "Success",
        description: "Address added successfully",
      });
      setOpen(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add address",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: AccountAddressFormValues) => {
      return await apiRequest("PATCH", `/api/addresses/${address?.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/accounts/${accountId}/addresses`] });
      toast({
        title: "Success",
        description: "Address updated successfully",
      });
      setOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update address",
        variant: "destructive",
      });
    },
  });

  const handleUseMyContactInfo = (checked: boolean) => {
    setUseMyContactInfo(checked);
    if (checked && user) {
      form.setValue("deliveryContactName", `${user.firstName || ''} ${user.lastName || ''}`.trim());
      form.setValue("deliveryContactPhone", user.phone || '');
      form.setValue("deliveryContactEmail", user.email || '');
    }
  };

  const onSubmit = (data: AccountAddressFormValues) => {
    if (mode === "create") {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  useEffect(() => {
    if (open && address && mode === "edit") {
      form.reset({
        isBilling: address.isBilling,
        isShipping: address.isShipping,
        locationName: address.locationName,
      deliveryContactName: address.deliveryContactName || "",
      deliveryContactPhone: address.deliveryContactPhone || "",
      deliveryContactEmail: address.deliveryContactEmail || "",
        line1: address.line1,
        line2: address.line2 || "",
        city: address.city,
        region: address.region,
        postalCode: address.postalCode,
        country: address.country,
        phone: address.phone || "",
        hasLoadingDock: address.hasLoadingDock || false,
        hasForklift: address.hasForklift || false,
        isDefaultShipping: address.isDefaultShipping || false,
      });
    } else if (open && mode === "create") {
      form.reset({
        isBilling: false,
        isShipping: true,
        locationName: "",
      deliveryContactName: "",
      deliveryContactPhone: "",
      deliveryContactEmail: "",
        line1: "",
        line2: "",
        city: "",
        region: "",
        postalCode: "",
        country: "US",
        phone: "",
        hasLoadingDock: false,
        hasForklift: false,
        isDefaultShipping: false,
      });
    }
  }, [open, address, mode, form]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-address-form">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Address" : "Edit Address"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Address Type Checkboxes */}
            <div className="space-y-3">
              <FormLabel className="text-sm font-medium">Address Type (select at least one) *</FormLabel>
              <div className="flex gap-6">
                <FormField
                  control={form.control}
                  name="isBilling"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                          data-testid="checkbox-is-billing"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Billing Address</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="isShipping"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                          data-testid="checkbox-is-shipping"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Shipping Address</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
              <FormMessage>{form.formState.errors.isBilling?.message}</FormMessage>
            </div>

            <FormField
              control={form.control}
              name="locationName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location Name *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="e.g., Warehouse A, Main Office, Distribution Center" 
                      {...field}
                      data-testid="input-location-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="line1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 1 *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Street address" 
                      {...field} 
                      data-testid="input-address1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="line2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 2 (Optional)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Suite, unit, building, floor, etc." 
                      {...field}
                      value={field.value || ""}
                      data-testid="input-address2"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="City" 
                        {...field} 
                        data-testid="input-city"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="region"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State/Province *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="State or Province" 
                        {...field} 
                        data-testid="input-region"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Postal code" 
                        {...field} 
                        data-testid="input-postal"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-country">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="US">United States</SelectItem>
                        <SelectItem value="CA">Canada</SelectItem>
                        <SelectItem value="MX">Mexico</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Phone number" 
                        {...field}
                        value={field.value || ""}
                        data-testid="input-phone"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Freight Capabilities */}
            <div className="space-y-3 pt-4 border-t">
              <FormLabel className="text-sm font-medium">Freight Capabilities (for freight quoting)</FormLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="hasLoadingDock"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                          data-testid="checkbox-loading-dock"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="flex items-center gap-1">
                          <Truck className="h-3 w-3" />
                          Loading Dock Available
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Facility has dock for loading/unloading freight
                        </p>
                      </div>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="hasForklift"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                          data-testid="checkbox-forklift"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="flex items-center gap-1">
                          <Forklift className="h-3 w-3" />
                          Forklift Available
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Facility has forklift for unloading
                        </p>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Shipping Contact Information */}
            {form.watch("isShipping") && (
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <FormLabel className="text-sm font-medium">Delivery Contact (for scheduling & delivery issues)</FormLabel>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="useMyContactInfo"
                      checked={useMyContactInfo}
                      onCheckedChange={handleUseMyContactInfo}
                      data-testid="checkbox-use-my-contact"
                    />
                    <Label htmlFor="useMyContactInfo" className="text-sm font-medium cursor-pointer">
                      I am the contact
                    </Label>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="deliveryContactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Who should we contact for deliveries?" 
                            {...field}
                            value={field.value || ""}
                            data-testid="input-delivery-contact-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="deliveryContactPhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Phone</FormLabel>
                          <FormControl>
                            <Input 
                              type="tel"
                              placeholder="Phone number" 
                              {...field}
                              value={field.value || ""}
                              data-testid="input-delivery-contact-phone"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="deliveryContactEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Email</FormLabel>
                          <FormControl>
                            <Input 
                              type="email"
                              placeholder="Email address" 
                              {...field}
                              value={field.value || ""}
                              data-testid="input-delivery-contact-email"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            )}

            {form.watch("isShipping") && (
              <FormField
                control={form.control}
                name="isDefaultShipping"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value || false}
                        onCheckedChange={field.onChange}
                        data-testid="checkbox-default"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Set as default shipping address</FormLabel>
                    </div>
                  </FormItem>
                )}
              />
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {(createMutation.isPending || updateMutation.isPending) 
                  ? "Saving..." 
                  : mode === "create" 
                    ? "Add Address" 
                    : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteSavedAddressButton({ addressId }: { addressId: string }) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("DELETE", `/api/users/addresses/${addressId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users/addresses"] });
      toast({
        title: "Success",
        description: "Address deleted successfully",
      });
      setOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete address",
        variant: "destructive",
      });
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`button-delete-address-${addressId}`}
      >
        <Trash2 className="h-3 w-3 mr-1" />
        Delete
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Address</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this address? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              deleteMutation.mutate();
            }}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DeleteAccountAddressButton({ addressId, accountId }: { addressId: string; accountId: string }) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("DELETE", `/api/addresses/${addressId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/accounts/${accountId}/addresses`] });
      toast({
        title: "Success",
        description: "Address deleted successfully",
      });
      setOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete address",
        variant: "destructive",
      });
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`button-delete-address-${addressId}`}
      >
        <Trash2 className="h-3 w-3 mr-1" />
        Delete
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Address</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this address? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              deleteMutation.mutate();
            }}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function SetDefaultSavedAddressButton({ addressId }: { addressId: string }) {
  const { toast } = useToast();

  const setDefaultMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("PUT", `/api/users/addresses/${addressId}`, { isDefault: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users/addresses"] });
      toast({
        title: "Success",
        description: "Default address updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to set default address",
        variant: "destructive",
      });
    },
  });

  return (
    <Button 
      variant="outline" 
      size="sm"
      onClick={() => setDefaultMutation.mutate()}
      disabled={setDefaultMutation.isPending}
      data-testid={`button-set-default-${addressId}`}
    >
      <Star className="h-3 w-3 mr-1" />
      {setDefaultMutation.isPending ? "Setting..." : "Set as Default"}
    </Button>
  );
}

function SetDefaultAccountAddressButton({ addressId, accountId }: { addressId: string; accountId: string }) {
  const { toast } = useToast();

  const setDefaultMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("PATCH", `/api/addresses/${addressId}`, { isDefaultShipping: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/accounts/${accountId}/addresses`] });
      toast({
        title: "Success",
        description: "Default shipping address updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to set default address",
        variant: "destructive",
      });
    },
  });

  return (
    <Button 
      variant="outline" 
      size="sm"
      onClick={() => setDefaultMutation.mutate()}
      disabled={setDefaultMutation.isPending}
      data-testid={`button-set-default-${addressId}`}
    >
      <Star className="h-3 w-3 mr-1" />
      {setDefaultMutation.isPending ? "Setting..." : "Set as Default"}
    </Button>
  );
}

